import Phaser from "phaser";
import { AssetManager } from "../runtime/assetManager";
import { EmbedBridge } from "../runtime/embedBridge";

/**
 * 起動シーン:
 *  - スタンドアロン: ./sample/project.pix.json を fetch
 *  - embed モード (?mode=embed): Studio から postMessage でプロジェクトを受信
 *  - manifest と画像アセットを Phaser に preload
 *  - 完了したら TitleScene へ
 */
export class BootScene extends Phaser.Scene {
  constructor() {
    super("BootScene");
  }

  private statusText!: Phaser.GameObjects.Text;
  private embed: EmbedBridge | null = null;

  create(): void {
    const { width, height } = this.scale;

    this.add
      .text(width / 2, height / 2 - 30, "Pix Engine", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "48px",
        color: "#ffffff",
      })
      .setOrigin(0.5);

    this.statusText = this.add
      .text(width / 2, height / 2 + 30, "Loading...", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "20px",
        color: "#888888",
      })
      .setOrigin(0.5);

    this.bootstrap().catch((err) => {
      console.error("[BootScene] init failed:", err);
      this.statusText.setText(`Error: ${(err as Error).message}`);
      this.embed?.send({ type: "log", level: "error", message: (err as Error).message });
    });
  }

  private async bootstrap(): Promise<void> {
    if (EmbedBridge.isEmbedMode()) {
      this.embed = new EmbedBridge();
      this.game.registry.set("embed", this.embed);
      this.embed.send({ type: "engineReady" });
      this.statusText.setText("Waiting for project from Studio...");

      const proj = await this.embed.waitForProject();
      const assets = new AssetManager(proj.manifest, proj.assetBaseUrl, proj.assets);
      this.game.registry.set("assets", assets);
      this.game.registry.set("baseUrl", proj.assetBaseUrl);
      this.game.registry.set("gameId", proj.manifest.id);
      this.game.registry.set("inMemoryScripts", proj.scripts);

      this.statusText.setText("Loading assets...");
      await this.preloadAssets(assets);
      this.embed.send({ type: "projectLoaded" });
      this.statusText.setText("Waiting for start signal...");

      await this.embed.waitForStart();
      this.scene.start("TitleScene", { gameId: proj.manifest.id });
      return;
    }

    // ?work=:id で公開作品を Portal API から取得して直接プレイ
    const params = new URLSearchParams(window.location.search);
    const workId = params.get("work");
    if (workId) {
      try {
        await this.loadPublishedWork(workId);
        return;
      } catch (e) {
        this.statusText.setText(`Failed to load work: ${(e as Error).message}`);
        return;
      }
    }

    // スタンドアロン
    const assets = await AssetManager.load("./sample");
    this.game.registry.set("assets", assets);
    this.game.registry.set("baseUrl", "./sample");
    this.game.registry.set("gameId", assets.getManifest().id);
    await this.preloadAssets(assets);
    this.scene.start("TitleScene", { gameId: assets.getManifest().id });
  }

  private async loadPublishedWork(workId: string): Promise<void> {
    const apiBase =
      (import.meta.env.VITE_API_URL as string | undefined) ?? "https://api.pixadv.net";
    this.statusText.setText(`Loading "${workId}"...`);
    const res = await fetch(`${apiBase}/api/portal/play/${encodeURIComponent(workId)}`);
    if (!res.ok) {
      const body = await res.text();
      throw new Error(`HTTP ${res.status} ${body.slice(0, 120)}`);
    }
    const data = (await res.json()) as {
      workId: string;
      title: string;
      manifest: import("@pix/shared").ProjectManifest;
      scripts: Record<string, string>;
      assetBaseUrl: string;
    };
    const assets = new AssetManager(data.manifest, data.assetBaseUrl, {});
    this.game.registry.set("assets", assets);
    this.game.registry.set("baseUrl", data.assetBaseUrl);
    this.game.registry.set("gameId", data.manifest.id);
    this.game.registry.set("inMemoryScripts", data.scripts);
    this.statusText.setText("Loading assets...");
    await this.preloadAssets(assets);
    this.scene.start("TitleScene", { gameId: data.manifest.id });
  }

  private async preloadAssets(assets: AssetManager): Promise<void> {
    for (const { name, url } of assets.listBgs()) {
      this.load.image(assets.getBgKey(name), url);
    }
    for (const { id, expression, url } of assets.listCharaSprites()) {
      this.load.image(assets.getCharaSpriteKey(id, expression), url);
    }
    for (const { id, expression, url } of assets.listFaces()) {
      this.load.image(assets.getFaceKey(id, expression), url);
    }

    this.load.on("progress", (p: number) => {
      this.statusText.setText(`Loading... ${Math.round(p * 100)}%`);
    });
    this.load.on("loaderror", (file: Phaser.Loader.File) => {
      console.warn("[BootScene] preload error:", file.key, file.src);
    });

    return new Promise((resolve) => {
      this.load.once("complete", () => resolve());
      this.load.start();
    });
  }
}
